import React, { useState, useRef, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAppStore } from '@/store/useAppStore'
import { useAuthStore } from '@/store/useAuthStore'
import type { User } from '@/services/api'

// Refresh credits every 2 minutes
const CREDITS_REFRESH_INTERVAL = 2 * 60 * 1000

// Global flag to prevent duplicate initial refresh
let creditsRefreshedOnce = false

// Format credits for display (compact format)
const formatCredits = (credits?: number): string => {
  if (credits === undefined || credits === null) return '0'

  const rounded = Math.round(credits * 10) / 10

  // Use K suffix for large numbers
  if (Math.abs(rounded) >= 10000) {
    return `${(rounded / 1000).toFixed(0)}K`
  }
  if (Math.abs(rounded) >= 1000) {
    return `${(rounded / 1000).toFixed(1)}K`
  }

  // Show integer for numbers >= 10
  if (Math.abs(rounded) >= 10) {
    return Math.round(rounded).toString()
  }

  // Show one decimal for smaller numbers
  return rounded.toFixed(1)
}

type PageType = 'prompts' | 'content' | 'library' | 'skills' | 'setup' | 'pricing'

const navSections = [
  {
    label: 'Monitor',
    items: [
      { id: 'prompts' as PageType, label: 'Prompt Tracking', icon: 'search', path: '/app/prompts' },
      { id: 'library' as PageType, label: 'Content Monitor', icon: 'file', path: '/app/library' }
    ]
  },
  {
    label: 'Create',
    items: [
      { id: 'content' as PageType, label: 'Content Studio', icon: 'edit', path: '/app/content' },
      { id: 'skills' as PageType, label: 'Skills Library', icon: 'sparkles', path: '/app/skills' }
    ]
  },
  {
    label: 'Account',
    items: [
      { id: 'pricing' as PageType, label: 'Pricing', icon: 'credit-card', path: '/app/pricing' }
    ]
  }
]

export const Sidebar: React.FC<{ user?: User | null; onLogout?: () => void }> = ({ user, onLogout }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const prompts = useAppStore((state) => state.prompts)
  const contentLibrary = useAppStore((state) => state.contentLibrary)
  const companyInfo = useAppStore((state) => state.companyInfo)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  // Refresh credits on mount and periodically
  useEffect(() => {
    // Only refresh once on initial mount
    if (!creditsRefreshedOnce) {
      creditsRefreshedOnce = true
      useAuthStore.getState().refreshCredits()
    }

    // Set up interval for periodic refresh
    const intervalId = setInterval(() => {
      useAuthStore.getState().refreshCredits()
    }, CREDITS_REFRESH_INTERVAL)

    return () => clearInterval(intervalId)
  }, []) // Empty dependency array - only run once on mount

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserMenu])

  const isActive = (path: string) => {
    if (path === '/app/content') return location.pathname.startsWith('/app/content')
    return location.pathname === path
  }

  const getCompanyInitial = () => {
    if (companyInfo?.name) {
      return companyInfo.name.charAt(0).toUpperCase()
    }
    return '?'
  }

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <div className="logo">
          <span>GEO Writer</span>
          <span className="logo-dot">.</span>
        </div>
      </div>

      <nav className="sidebar-nav">
        {navSections.map((section) => (
          <div key={section.label} className="nav-section">
            <div className="nav-label">{section.label}</div>
            {section.items.map((item) => {
              const badgeCount = item.id === 'prompts' 
                ? prompts.length 
                : item.id === 'library' 
                ? contentLibrary.length 
                : undefined
              
              // Show indicator for setup page if no company info
              const showSetupIndicator = item.id === 'setup' && !companyInfo
              
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                >
                  <svg className="icon"><use href={`#icon-${item.icon}`}></use></svg>
                  {item.label}
                  {badgeCount !== undefined && badgeCount > 0 && (
                    <span className="nav-badge">{badgeCount}</span>
                  )}
                  {showSetupIndicator && (
                    <span className="nav-badge nav-badge-alert">!</span>
                  )}
                </Link>
              )
            })}
          </div>
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-footer-section">
          <div className="sidebar-footer-label">Company</div>
          <div
            className={`sidebar-user sidebar-user--company ${companyInfo ? 'is-ready' : 'is-empty'}`}
            onClick={() => navigate('/app/setup')}
            title={companyInfo ? 'Click to manage company info' : 'Click to set up your company'}
            role="button"
            tabIndex={0}
          >
            <div className="sidebar-avatar sidebar-avatar--company">{getCompanyInitial()}</div>
            <div className="sidebar-info">
              <div className="sidebar-name">{companyInfo?.name || 'Set up company'}</div>
              <div className="sidebar-domain">{companyInfo?.domain || 'Click to configure'}</div>
            </div>
            {companyInfo ? (
              <svg className="icon sidebar-user-icon" aria-hidden="true">
                <use href="#icon-chevron"></use>
              </svg>
            ) : (
              <svg className="icon sidebar-user-icon" aria-hidden="true">
                <use href="#icon-plus"></use>
              </svg>
            )}
          </div>
        </div>

        <div className="sidebar-footer-section">
          <div className="sidebar-footer-label">Personal</div>
          <div
            ref={userMenuRef}
            className="sidebar-user sidebar-user--clickable"
          >
            <div className="sidebar-user-main">
              <div className="sidebar-avatar sidebar-avatar--person">
                {user?.full_name?.[0] || user?.username?.[0] || 'U'}
              </div>
              <div className="sidebar-info">
                <div className="sidebar-name">{user?.full_name || user?.username || 'User'}</div>
                <div className="sidebar-domain">{user?.email || ''}</div>
              </div>
              <div
                className={`sidebar-credits ${(user?.credits ?? 0) < 0 ? 'sidebar-credits--negative' : ''}`}
                title="Available credits - Click to view details"
              >
                <svg className="icon"><use href="#icon-sparkles"></use></svg>
                <span>{formatCredits(user?.credits)}</span>
              </div>
            </div>
            <button
              className="sidebar-user-menu-btn"
              onClick={(e) => {
                e.stopPropagation()
                setShowUserMenu(!showUserMenu)
              }}
              title="More options"
            >
              <svg className="icon"><use href="#icon-chevron"></use></svg>
            </button>
            {showUserMenu && (
              <div className="sidebar-user-menu">
                {onLogout && (
                  <button
                    className="sidebar-user-menu-item sidebar-user-menu-item--danger"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowUserMenu(false)
                      onLogout()
                    }}
                  >
                    <svg className="icon"><use href="#icon-arrow-left"></use></svg>
                    Logout
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </aside>
  )
}
